// =============================================================================
// Hosted-agent lifecycle requests (B10/B13 — HERMES wave 2)
//
// The backend never touches Docker (plan B10). "Upgrade now", restart, stop,
// start and reset in the portal only CREATE a durable request document; the deploy
// worker on the customer VM (`poc_deploy_customer.py hermes-lifecycle-worker`,
// the ONE deploy chain) claims it with the ops token and runs the B13 flow —
// pause → drain → recreate → verify → auto-rollback on red — reporting
// progress and the terminal outcome back here. The portal only polls.
//
// Status machine: pending → claimed → running → succeeded | failed | rolled_back
//                 pending → cancelled (only pending can be cancelled)
// =============================================================================

import axios from "axios"

import { api } from "@/lib/api-client"

export type HostedAgentLifecycleAction =
  | "upgrade"
  | "rollback"
  | "restart"
  | "stop"
  | "start"
  | "reset"
  | "decommission"

export type HostedAgentLifecycleStatus =
  | "pending"
  | "claimed"
  | "running"
  | "succeeded"
  | "failed"
  | "rolled_back"
  | "cancelled"

/**
 * Same fields as the deploy manifest's `hermesAgents[].image*`. A request
 * sends exactly one of them; the backend echoes BOTH keys back with the unused
 * one `null` (`{ imageDigest: null, imageTag: "v1.2.0" }`), so consumers must
 * test the values, never key presence.
 */
export type HostedAgentLifecycleImage = {
  imageDigest?: string | null
  imageTag?: string | null
}

export type HostedAgentLifecycleOptions = {
  drain_timeout?: number
  timeout?: number
}

export type HostedAgentLifecycleResultCode =
  | "completed"
  | "rolled_back"
  | "rollback_failed"
  | "manual-rollback"
  | "failed"
  | "cancelled"
  | "lease_expired"
  | "unknown_agent"

export type HostedAgentLifecycleResult = {
  code: HostedAgentLifecycleResultCode | string
  from_image?: string | null
  to_image?: string | null
  exit_code?: number | null
}

export type HostedAgentLifecycleLogStep =
  | "pin"
  | "pause"
  | "drain"
  | "recreate"
  | "healthz"
  | "handshake"
  | "selftest"
  | "resume"
  | "rollback"

export type HostedAgentLifecycleLogEntry = {
  at: string
  level: "info" | "warn" | "error"
  step: HostedAgentLifecycleLogStep | string
  message: string
}

export type HostedAgentLifecycleRequest = {
  /** "lcr:<uuid>" */
  id: string
  tenant_id?: string
  agent_id: string
  agent_slug: string
  action: HostedAgentLifecycleAction
  image: HostedAgentLifecycleImage | null
  options: HostedAgentLifecycleOptions
  status: HostedAgentLifecycleStatus
  requested_by: string
  requested_at: string
  claimed_by: string | null
  claimed_at: string | null
  lease_expires_at: string | null
  started_at: string | null
  finished_at: string | null
  result: HostedAgentLifecycleResult | null
  detail: string
  log: HostedAgentLifecycleLogEntry[]
  revision: number
}

/** Compact pointer the single-agent GET carries so buttons can be disabled. */
export type HostedAgentLifecycleSummary = {
  active_request_id: string
  status: HostedAgentLifecycleStatus
  action: HostedAgentLifecycleAction
}

export type HostedAgentLifecycleRequestInput = {
  action: HostedAgentLifecycleAction
  image?: HostedAgentLifecycleImage
  options?: HostedAgentLifecycleOptions
}

export type HostedAgentLifecycleErrorCode =
  | "lifecycle_request_in_flight"
  | "not_cancellable"
  | "not_lease_owner"
  | "unknown_agent"

function rethrowLifecycleError(error: unknown): never {
  if (axios.isAxiosError(error) && error.response) {
    const data = error.response.data as { code?: string; error?: string } | undefined
    const code = data?.code
    if (
      code === "lifecycle_request_in_flight" ||
      code === "not_cancellable" ||
      code === "not_lease_owner" ||
      code === "unknown_agent"
    ) {
      throw new Error(`${code}: ${data?.error ?? "lifecycle request rejected"}`, {
        cause: { code },
      })
    }
  }
  throw error
}

/** Narrow an unknown error to a lifecycle error code, if it is one. */
export function getHostedAgentLifecycleErrorCode(
  error: unknown
): HostedAgentLifecycleErrorCode | null {
  if (error instanceof Error && typeof error.cause === "object" && error.cause !== null) {
    const code = (error.cause as { code?: unknown }).code
    if (
      code === "lifecycle_request_in_flight" ||
      code === "not_cancellable" ||
      code === "not_lease_owner" ||
      code === "unknown_agent"
    ) {
      return code
    }
  }
  return null
}

/**
 * Queue a lifecycle request for one agent (id or slug). 409
 * `lifecycle_request_in_flight` when the agent already has a non-terminal
 * request (or, for upgrade/rollback, another agent is mid-upgrade — B13 "one
 * agent at a time").
 */
export async function createHostedAgentLifecycleRequest(
  agentRef: string,
  input: HostedAgentLifecycleRequestInput
): Promise<HostedAgentLifecycleRequest> {
  try {
    const response = await api.post(
      `/admin/hosted-agents/${encodeURIComponent(agentRef)}/lifecycle-requests`,
      input
    )
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to queue the lifecycle request")
    }
    return data.request as HostedAgentLifecycleRequest
  } catch (error) {
    rethrowLifecycleError(error)
  }
}

/** Newest-first history for one agent. */
export async function listHostedAgentLifecycleRequests(
  agentRef: string,
  limit = 20
): Promise<HostedAgentLifecycleRequest[]> {
  const response = await api.get(
    `/admin/hosted-agents/${encodeURIComponent(agentRef)}/lifecycle-requests?limit=${limit}`
  )
  const data = response.data || response
  return Array.isArray(data?.requests) ? (data.requests as HostedAgentLifecycleRequest[]) : []
}

/**
 * Tenant-wide view, optionally filtered to a status set — the table and the
 * map use it to show one status chip per agent with a single request.
 */
export async function listTenantHostedAgentLifecycleRequests(
  statuses?: HostedAgentLifecycleStatus[]
): Promise<HostedAgentLifecycleRequest[]> {
  const query = statuses && statuses.length > 0 ? `?status=${statuses.join(",")}` : ""
  const response = await api.get(`/admin/hosted-agents/lifecycle-requests${query}`)
  const data = response.data || response
  return Array.isArray(data?.requests) ? (data.requests as HostedAgentLifecycleRequest[]) : []
}

export async function getHostedAgentLifecycleRequest(
  requestId: string
): Promise<HostedAgentLifecycleRequest> {
  const response = await api.get(
    `/admin/hosted-agents/lifecycle-requests/${encodeURIComponent(requestId)}`
  )
  const data = response.data || response
  return data.request as HostedAgentLifecycleRequest
}

/** Only a `pending` request can be cancelled; otherwise 409 `not_cancellable`. */
export async function cancelHostedAgentLifecycleRequest(
  requestId: string
): Promise<HostedAgentLifecycleRequest> {
  try {
    const response = await api.post(
      `/admin/hosted-agents/lifecycle-requests/${encodeURIComponent(requestId)}/cancel`
    )
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to cancel the lifecycle request")
    }
    return data.request as HostedAgentLifecycleRequest
  } catch (error) {
    rethrowLifecycleError(error)
  }
}
